import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  EmoneyIssuanceRequestDto,
  TransactionDto,
} from "@/features/wallet/api/dto";

/**
 * Émet de la monnaie électronique (`POST /EmoneyIssuances/issue`).
 * Idempotence obligatoire. Exécuté après une demande `EMONEY_ISSUANCE`
 * APPROVED (quatre yeux) — pas au simple clic maker.
 */
export const useIssueEmoney = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "emoney", "issue"],
    mutationFn: (body: EmoneyIssuanceRequestDto) =>
      mutateIdempotent<TransactionDto>({
        scopeId: "wallet.emoney.issue",
        url: walletEndpoints.emoneyIssuances.issue,
        body,
      }),
    onSuccess: (data) => {
      return invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(data.id),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useIssueEmoney failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
